import React, { useCallback, useEffect, useMemo, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import styled from "styled-components";
import { decryptFromBackend } from "../../../utils/urlDecryptor";
import ChatHeader from "./ChatHeader";
import ChatInput from "./ChatInput";
import ChatMessage from "./ChatMessage";
import ConsultationReminderModal from "./ConsultationReminderModal";
import FeedbackScreen from "./FeedbackScreen";
import PatientCard from "./PatientCard";

import { useChatMessages } from "./hooks/useChatMessages";
import { useKeyboardOffset } from "./hooks/useKeyboardOffset";
import { useTeleconsultation } from "./hooks/useTeleconsultation";
import { useZegoChat } from "./hooks/useZegoChat";
import { isSystemMessage } from "./utils/chatHelpers";

// ─── Styled Components ────────────────────────────────────────────────────────

const ChatContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100dvh;
  height: 100vh;
  overflow: hidden;
  align-self: center;
`;

const MessagesContainer = styled.div`
  flex: 1;
  padding: 120px 16px 130px;
  overflow-y: auto;
  background-image: url("/images/bg-chat.png");
  background-size: cover;
  background-repeat: no-repeat;
  background-position: center;
  display: flex;
  flex-direction: column;
  -webkit-overflow-scrolling: touch;
  justify-content: center;
`;

const ChatInputContainer = styled.div`
  position: fixed;
  bottom: 0;
  bottom: env(safe-area-inset-bottom);
  background: #ffffff;
  padding: 0 0 calc(env(safe-area-inset-bottom) + 8px);
  box-shadow: 0 -2px 4px rgba(0, 0, 0, 0.05);
  z-index: 100;
  transform: translateY(0);
  transition: transform 0.2s ease;
  width: 100%;
`;

// ─── Main Component ───────────────────────────────────────────────────────────

const Chat: React.FC = () => {
  const { conversationID, token } = useParams<{
    conversationID: string;
    token: string;
  }>();

  const decryptedToken = useMemo(() => {
    if (!token) return "";
    try {
      return decryptFromBackend(token);
    } catch (err) {
      console.error("Failed to decrypt token:", err);
      return "";
    }
  }, [token]);

  // ─── Hooks ────────────────────────────────────────────────────────────────

  const {
    teleconsultationData,
    isExpired,
    showReminder,
    setShowReminder,
    showRating,
    setShowRating,
  } = useTeleconsultation(conversationID, decryptedToken);

  const { isLoggedIn, userId, userDokter, groupId } =
    useZegoChat(teleconsultationData);

  const { messages, replyTo, setReplyTo, handleSend, handleSendMedia } =
    useChatMessages({
      isLoggedIn,
      groupId,
      userId,
      userDokter,
      conversationID,
      decryptedToken,
    });

  const keyboardOffset = useKeyboardOffset();

  // ─── Listen for consultation end event ───────────────────────────────────
  // useChatMessages dispatches 'consultation:ended' when it detects end|{id}

  const navigate = useNavigate();
  useEffect(() => {
    window.history.pushState(null, '', window.location.href);

    const handlePopState = () => {
      navigate('/home', { replace: true });
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [navigate]);

  useEffect(() => {
    const handleConsultationEnded = () => {
      setShowRating(true);
    };
    window.addEventListener("consultation:ended", handleConsultationEnded);
    return () =>
      window.removeEventListener("consultation:ended", handleConsultationEnded);
  }, [setShowRating]);

  // ─── Refs ─────────────────────────────────────────────────────────────────

  const messagesEndRef = useRef<HTMLDivElement | null>(null);
  const messagesContainerRef = useRef<HTMLDivElement | null>(null);
  const chatInputRef = useRef<HTMLDivElement | null>(null);
  const messageRefsMap = useRef<{ [key: string]: HTMLDivElement | null }>({});
  const [highlightedMessageID, setHighlightedMessageID] = React.useState<
    string | null
  >(null);

  // ─── Scroll to bottom ─────────────────────────────────────────────────────

  const scrollToBottom = useCallback(() => {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: "auto" });
      });
    });
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, keyboardOffset, scrollToBottom]);

  // ─── Reply quote click ────────────────────────────────────────────────────

  const handleReplyQuoteClick = (messageID: string) => {
    setHighlightedMessageID(messageID);
    const messageElement = messageRefsMap.current[messageID];
    if (messageElement) {
      messageElement.scrollIntoView({ behavior: "smooth", block: "center" });
    }
    setTimeout(() => setHighlightedMessageID(null), 2000);
  };

  // ─── Loading state ────────────────────────────────────────────────────────

  if (!teleconsultationData) {
    return (
      <div style={{ padding: 16, textAlign: "center" }}>
        Memuat data telekonsultasi...
      </div>
    );
  }

  // ─── Render ───────────────────────────────────────────────────────────────

  return (
    <ChatContainer>
      <ChatHeader
        doctorAvatar={
          teleconsultationData.practitioner_avatar_url ||
          "https://m.linksehat.com/assets/img/users/female-avatar.png"
        }
        doctorName={teleconsultationData.practitioner_name || "Dokter"}
        doctorSpecialty={
          teleconsultationData.practitioner_speciality || "Spesialis Umum"
        }
        consultationMedia={teleconsultationData.teleconsultation_type || "CHAT"}
        startTime={teleconsultationData.teleconsultation_start_time}
        duration={teleconsultationData.teleconsultation_duration_timeout}
        isExpired={isExpired}
        consultationId={teleconsultationData.teleconsultation_id || ""}
        token={decryptedToken}
        serverTime={teleconsultationData.server_time}
      />

      <div
        style={{
          display: "flex",
          justifyContent: "center",
          padding: "12px 0",
          boxShadow: "2px 2px 2px rgba(0,0,0,0.1)",
          zIndex: 100,
        }}
      >
        <PatientCard
          name={teleconsultationData.patient_name || "Nama Pasien"}
          insurance={teleconsultationData.patient_insurance || "Insurance"}
        />
      </div>

      <MessagesContainer ref={messagesContainerRef}>
        {messages
          .filter(
            (msg) =>
              typeof msg.message === "string" && !isSystemMessage(msg.message),
          )
          .map((msg) => (
            <ChatMessage
              key={`${msg.messageID}-${msg.timestamp}`}
              {...msg}
              onReply={() => setReplyTo(msg as any)}
              doctorName={teleconsultationData.practitioner_name || "Dokter"}
              doctorSpecialty={
                teleconsultationData.practitioner_speciality || "Spesialis Umum"
              }
              avatar={teleconsultationData.practitioner_avatar_url}
              userDokter={userDokter}
              patientName={teleconsultationData.patient_name}
              ref={(element: any) => {
                if (element) messageRefsMap.current[msg.messageID] = element;
              }}
              isHighlighted={highlightedMessageID === msg.messageID}
              onReplyQuoteClick={handleReplyQuoteClick}
              replyTo={msg.replyTo}
            />
          ))}
        <div ref={messagesEndRef} />
      </MessagesContainer>

      {!isExpired && (
        <ChatInputContainer
          ref={chatInputRef}
          style={{ bottom: keyboardOffset }}
        >
          <ChatInput
            onSend={handleSend}
            onSendMedia={handleSendMedia}
            replyTo={replyTo}
            onCancelReply={() => setReplyTo(null)}
            doctorName={teleconsultationData.practitioner_name || "Dokter"}
            isDoctor={false}
          />
        </ChatInputContainer>
      )}

      <ConsultationReminderModal
        visible={showReminder}
        onClose={() => setShowReminder(false)}
      />

      <FeedbackScreen
        doctorId={teleconsultationData.practitioner_id || ""}
        teleconsultId={teleconsultationData.teleconsultation_id || ""}
        teleconsultationCode={teleconsultationData.teleconsultation_code || ""}
        patientId={teleconsultationData.patient_id || ""}
        token={decryptedToken}
        visible={showRating}
        onClose={() => setShowRating(false)}
      />
    </ChatContainer>
  );
};

export default Chat;
